import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Product, Category } from '@/types';
import { Badge } from '@/components/ui/badge';
import { Search, Tag, ChevronLeft, ChevronRight, AlertCircle } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { useApp } from '@/contexts/AppContext';
import { useIsMobile } from '@/hooks/use-mobile';
import CategoryCarousel from './CategoryCarousel';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";

interface ProductSelectorProps {
  products?: Product[];
  categories?: Category[];
  onSelectProduct?: (product: Product) => void;
  orderId?: string;
  onClose?: () => void;
}

const ProductSelector: React.FC<ProductSelectorProps> = ({ 
  products: propProducts, 
  categories: propCategories, 
  onSelectProduct,
  orderId,
  onClose 
}) => {
  const { addItemToOrder, products: contextProducts, categories: contextCategories } = useApp();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [debugInfo, setDebugInfo] = useState<string>('');
  const isMobile = useIsMobile();
  
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = isMobile ? 8 : 12;
  
  const products = propProducts || contextProducts || [];
  const categories = propCategories || contextCategories || [];

  useEffect(() => {
    const debugDetails = `Produtos carregados: ${products.length}, Categorias carregadas: ${categories.length}`;
    console.log(debugDetails);
    setDebugInfo(debugDetails);
    
    let results = products;

    if (searchTerm) {
      results = results.filter(product =>
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (product.code && product.code.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (product.brand && product.brand.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    if (selectedCategory) {
      results = results.filter(product => product.categoryId === selectedCategory);
    }

    setFilteredProducts(results);
    setCurrentPage(1);
  }, [searchTerm, selectedCategory, products, categories]);

  const handleCategoryClick = (categoryId: string | null) => {
    setSelectedCategory(categoryId);
  };

  const handleProductClick = (product: Product) => {
    if (orderId && addItemToOrder) {
      addItemToOrder(orderId, product, 1);
      toast({
        title: "Produto adicionado",
        description: `${product.name} foi adicionado ao pedido`,
      });
      
      if (onClose) {
        onClose();
      }
    } else if (onSelectProduct) {
      onSelectProduct(product);
    }
  };

  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredProducts.slice(indexOfFirstItem, indexOfLastItem);

  const pageNumbers = [];
  
  if (totalPages <= 3) {
    for (let i = 1; i <= totalPages; i++) {
      pageNumbers.push(i);
    }
  } else {
    pageNumbers.push(1);
    
    if (currentPage > 2) {
      pageNumbers.push('ellipsis');
    }
    
    if (currentPage !== 1 && currentPage !== totalPages) {
      pageNumbers.push(currentPage);
    }
    
    if (currentPage < totalPages - 1) {
      pageNumbers.push('ellipsis');
    }
    
    pageNumbers.push(totalPages);
  }

  const cardColors = [
    'from-blue-500 to-blue-400',
    'from-purple-500 to-purple-400',
    'from-pink-500 to-pink-400',
    'from-orange-500 to-orange-400',
    'from-green-500 to-green-400',
    'from-cyan-500 to-cyan-400',
  ];

  return (
    <Card className="w-full overflow-hidden">
      <CardContent className={`space-y-2 p-0 sm:p-4 ${isMobile ? 'pt-2 px-2' : 'sm:space-y-3'}`}>
        <div className="flex items-center space-x-2 px-2 py-1">
          <Search className="h-4 w-4 text-gray-500 flex-shrink-0" />
          <Input
            type="search"
            placeholder="Buscar produto..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full h-8"
          />
        </div>

        {categories.length > 0 ? (
          <div className="px-2">
            <CategoryCarousel
              categories={categories}
              selectedCategory={selectedCategory}
              onSelectCategory={handleCategoryClick}
            />
          </div>
        ) : (
          <div className="flex items-center justify-center p-2 text-amber-600 bg-amber-50 rounded-md mx-2">
            <AlertCircle className="h-4 w-4 mr-2" />
            <span className="text-xs">Nenhuma categoria disponível</span>
          </div>
        )}
          
        {products.length > 0 ? (
          <div className="grid grid-cols-2 gap-1 px-1">
            {currentItems.map((product, index) => (
              <Card 
                key={product.id} 
                className="cursor-pointer hover:shadow transition-all overflow-hidden border-0 shadow-sm" 
                onClick={() => handleProductClick(product)}
              >
                <div className={`h-1 bg-gradient-to-r ${cardColors[index % cardColors.length]}`} />
                <CardContent className="p-1.5">
                  <div className="flex flex-col space-y-0.5">
                    <h3 className="text-xs font-semibold line-clamp-1">{product.name}</h3>
                    <p className="text-[10px] text-gray-500 truncate">{product.description || 'Sem descrição'}</p>
                    <div className="flex items-center space-x-1">
                      <Tag className="h-2.5 w-2.5 text-gray-400" />
                      <span className="text-gray-500 text-[10px]">{product.brand || 'Sem marca'}</span>
                    </div>
                    <Badge className={`bg-gradient-to-r ${cardColors[index % cardColors.length]} border-0 text-white text-[10px] font-medium mt-0.5 w-fit hover:opacity-90 px-1.5 py-0.5 h-4`}>
                      R$ {product.price ? product.price.toFixed(2) : '0.00'}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center p-4 text-amber-600 bg-amber-50 rounded-md mx-2">
            <AlertCircle className="h-5 w-5 mb-2" />
            <p className="text-sm font-medium">Nenhum produto disponível</p>
            <p className="text-xs text-center mt-1">
              Sincronize produtos no menu de configurações ou verifique se a API está configurada corretamente.
            </p>
            {debugInfo && (
              <div className="mt-2 p-2 bg-gray-100 rounded text-xs text-gray-600 w-full">
                <pre className="whitespace-pre-wrap break-all">{debugInfo}</pre>
              </div>
            )}
          </div>
        )}
          
        {products.length > 0 && filteredProducts.length === 0 && (
          <div className="text-center py-2">
            <p className="text-gray-500 text-sm">Nenhum produto encontrado com esse filtro.</p>
          </div>
        )}
          
        {totalPages > 1 && (
          <Pagination className="mt-0">
            <PaginationContent className="gap-0">
              {isMobile ? (
                <>
                  <PaginationItem>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-xs"
                      onClick={() => setCurrentPage(currentPage > 1 ? currentPage - 1 : 1)}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft className="h-3 w-3" />
                      <span>Anterior</span>
                    </Button>
                  </PaginationItem>
                    
                  <PaginationItem>
                    <div className="px-2 text-xs flex items-center">
                      {currentPage}/{totalPages}
                    </div>
                  </PaginationItem>
                    
                  <PaginationItem>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-xs"
                      onClick={() => setCurrentPage(currentPage < totalPages ? currentPage + 1 : totalPages)}
                      disabled={currentPage === totalPages}
                    >
                      <span>Próximo</span>
                      <ChevronRight className="h-3 w-3" />
                    </Button>
                  </PaginationItem>
                </>
              ) : (
                <>
                  <PaginationItem>
                    <PaginationPrevious 
                      href="#" 
                      onClick={(e) => {
                        e.preventDefault();
                        if (currentPage > 1) setCurrentPage(currentPage - 1);
                      }}
                      className={currentPage === 1 ? 'pointer-events-none opacity-50' : ''}
                    />
                  </PaginationItem>
                    
                  {pageNumbers.map((pageNumber, index) => (
                    pageNumber === 'ellipsis' ? (
                      <PaginationItem key={`ellipsis-${index}`}>
                        <span className="flex h-9 w-9 items-center justify-center">...</span>
                      </PaginationItem>
                    ) : (
                      <PaginationItem key={`page-${pageNumber}`}>
                        <PaginationLink 
                          href="#" 
                          onClick={(e) => {
                            e.preventDefault();
                            setCurrentPage(Number(pageNumber));
                          }}
                          isActive={currentPage === pageNumber}
                        >
                          {pageNumber}
                        </PaginationLink>
                      </PaginationItem>
                    )
                  ))}
                    
                  <PaginationItem>
                    <PaginationNext 
                      href="#" 
                      onClick={(e) => {
                        e.preventDefault();
                        if (currentPage < totalPages) setCurrentPage(currentPage + 1);
                      }}
                      className={currentPage === totalPages ? 'pointer-events-none opacity-50' : ''}
                    />
                  </PaginationItem>
                </>
              )}
            </PaginationContent>
          </Pagination>
        )}
          
        {products.length > 0 && (
          <div className="text-[10px] text-gray-500 text-center pb-1">
            Mostrando {filteredProducts.length > 0 ? `${indexOfFirstItem + 1}-${Math.min(indexOfLastItem, filteredProducts.length)} de ${filteredProducts.length}` : '0'} produtos
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ProductSelector;
